import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { SunIcon, SunsetIcon, MoonIcon, StarIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { TimeDistributionItem, WeeklyHeatmapItem } from '../services/metricsApi.tsx';

type HorariosTabProps = {
  timeDistribution: TimeDistributionItem[];
  weeklyHeatmap: WeeklyHeatmapItem[];
};

// Interface para período do dia
interface PeriodData {
  leads: number;
  agendamentos: number;
  taxaAgendamento: number;
}

// Interface para todos os períodos
interface PeriodsByTime {
  morning: PeriodData;
  afternoon: PeriodData;
  night: PeriodData;
  dawn: PeriodData;
}

export const HorariosTab: React.FC<HorariosTabProps> = ({
  timeDistribution,
  weeklyHeatmap
}) => {
  const { isDark } = useTheme();
  const weekdayMap: { [key: number]: string } = {
    0: "Domingo",
    1: "Segunda",
    2: "Terça",
    3: "Quarta",
    4: "Quinta",
    5: "Sexta",
    6: "Sábado"
  };
  const orderedWeekdays = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
  const heatmapSlots = ["8-10", "10-12", "12-14", "14-16", "16-18", "18-20"];
  // Estado para armazenar os dados por período
  const [periodData, setPeriodData] = useState<PeriodsByTime>({
    morning: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
    afternoon: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
    night: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
    dawn: { leads: 0, agendamentos: 0, taxaAgendamento: 0 }
  });

  // Processar time distribution para calcular os dados por período
  useEffect(() => {
    if (!timeDistribution || timeDistribution.length === 0) return;

    // Definir faixas horárias para cada período
    const periodRanges = {
      morning: ['8-9', '9-10', '10-11', '11-12'],
      afternoon: ['12-13', '13-14', '14-15', '15-16', '16-17', '17-18'],
      night: ['18-19', '19-20', '20-21', '21-22', '22-23', '23-0'],
      dawn: ['0-1', '1-2', '2-3', '3-4', '4-5', '5-6', '6-7', '7-8']
    };

    // Inicializar contadores
    const counts: PeriodsByTime = {
      morning: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
      afternoon: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
      night: { leads: 0, agendamentos: 0, taxaAgendamento: 0 },
      dawn: { leads: 0, agendamentos: 0, taxaAgendamento: 0 }
    };

    // Somar dados por período
    timeDistribution.forEach(item => {
      // Determinar em qual período esta hora se encaixa
      Object.entries(periodRanges).forEach(([period, hoursRange]) => {
        if (hoursRange.includes(item.hora)) {
          counts[period as keyof PeriodsByTime].leads += item.leads;
          counts[period as keyof PeriodsByTime].agendamentos += item.agendamentos;
        }
      });
    });

    // Calcular taxas de agendamento
    Object.keys(counts).forEach(period => {
      const p = period as keyof PeriodsByTime;
      const periodLeads = counts[p].leads;
      const periodAgendamentos = counts[p].agendamentos;

      counts[p].taxaAgendamento = periodLeads > 0
        ? (periodAgendamentos / periodLeads) * 100
        : 0;
    });

    setPeriodData(counts);
  }, [timeDistribution]);

  // Função para obter a intensidade da cor com base no valor (para heatmap)
  const getColorClass = (value: number) => {
    // Escala ajustada para máximo de 10 leads - usando cores da marca
    if (value <= 0) return isDark ? "bg-gray-800/50 text-gray-600" : "bg-gray-50 text-gray-400";
    // Azul da marca: var(--sminy-primary)
    if (value <= 2) return isDark ? "bg-sminy-primary/10 text-sminy-primary" : "bg-sminy-primary/10 text-sminy-primary";
    if (value <= 4) return isDark ? "bg-sminy-primary/30 text-blue-100" : "bg-sminy-primary/30 text-blue-900";
    if (value <= 6) return isDark ? "bg-sminy-primary/60 text-white" : "bg-sminy-primary/60 text-white";
    if (value <= 8) return isDark ? "bg-sminy-primary/80 text-white" : "bg-sminy-primary/80 text-white";
    return "bg-sminy-primary text-white"; // 10+
  };

  // Formatar dados do heatmap para visualização
  const formatHeatmapData = () => {
    const groupedByDay: { [key: string]: { diaSemana: string, [key: string]: number | string } } = {};

    orderedWeekdays.forEach((dia) => {
      groupedByDay[dia] = { diaSemana: dia };
      heatmapSlots.forEach((slot) => {
        groupedByDay[dia][slot] = 0;
      });
    });

    weeklyHeatmap.forEach(item => {
      const dia = weekdayMap[item.diaSemana];

      if (!groupedByDay[dia]) {
        groupedByDay[dia] = { diaSemana: dia };
      }

      groupedByDay[dia][item.faixa] = item.totalLeads;
    });

    return orderedWeekdays.map((dia) => groupedByDay[dia]);
  };

  const heatmapData = formatHeatmapData();
  const peakHeatmapSlot = weeklyHeatmap.reduce<WeeklyHeatmapItem | null>((best, item) => {
    if (item.totalLeads <= 0) {
      return best;
    }

    if (!best || item.totalLeads > best.totalLeads) {
      return item;
    }

    return best;
  }, null);

  const dominantPeriod = [
    { key: 'morning', label: 'Manhã', data: periodData.morning },
    { key: 'afternoon', label: 'Tarde', data: periodData.afternoon },
    { key: 'night', label: 'Noite', data: periodData.night },
    { key: 'dawn', label: 'Madrugada', data: periodData.dawn },
  ].reduce<{ key: string; label: string; data: PeriodData } | null>((best, item) => {
    if (item.data.leads <= 0) {
      return best;
    }

    if (!best || item.data.leads > best.data.leads) {
      return item;
    }

    return best;
  }, null);

  return (
    <div className="space-y-6">
      {/* Distribuição por Horário do Dia */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Distribuição por Horário</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Volume de leads e agendamentos por hora do dia</p>
        </div>

        <div className="p-6 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={timeDistribution}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? '#374151' : '#f3f4f6'} />
              <XAxis
                dataKey="hora"
                fontSize={12}
                axisLine={false}
                tickLine={false}
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                dy={10}
              />
              <YAxis
                fontSize={12}
                axisLine={false}
                tickLine={false}
                stroke={isDark ? '#9ca3af' : '#6b7280'}
              />
              <Tooltip
                formatter={(value) => [`${value}`, '']}
                labelFormatter={(label) => `Horário: ${label}`}
                cursor={{ fill: isDark ? '#374151' : '#f3f4f6', opacity: 0.4 }}
                contentStyle={{
                  backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  borderColor: isDark ? '#374151' : '#e5e7eb',
                  borderRadius: '0.75rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  padding: '12px',
                  border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                  color: isDark ? '#fff' : '#000'
                }}
                itemStyle={{ paddingBottom: 4 }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{ paddingBottom: '20px', fontSize: '14px' }}
              />
              <Bar
                dataKey="leads"
                name="Leads"
                fill="var(--sminy-primary)"
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
              <Bar
                dataKey="agendamentos"
                name="Agendamentos"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Heatmap Semanal */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Heatmap Semanal</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Concentração de leads por dia e horário</p>
        </div>

        <div className="p-6 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 overflow-x-auto">
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'bg-gray-900/50' : 'bg-gray-50'}>
                    <th className={`p-3 text-left font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Dia</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>8-10h</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>10-12h</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>12-14h</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>14-16h</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>16-18h</th>
                    <th className={`p-3 text-center font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>18-20h</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {heatmapData.map((day: any, idx) => (
                    <tr key={idx} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                      <td className={`p-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {day.diaSemana}
                      </td>
                      {heatmapSlots.map((timeSlot) => {
                        const value = day[timeSlot] || 0;
                        return (
                          <td key={timeSlot} className="p-1 text-center">
                            <div className={`mx-auto w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${getColorClass(value)}`}>
                              {value > 0 ? value : '-'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className={`p-5 rounded-xl border ${isDark ? 'bg-gray-700/20 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Legenda</h4>

              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs mr-3 ${isDark ? 'bg-gray-800/50 text-gray-600' : 'bg-gray-100 text-gray-400'}`}>-</div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sem atividade</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs mr-3 ${isDark ? 'bg-sminy-primary/10 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'}`}>1-2</div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Baixa atividade</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs mr-3 ${isDark ? 'bg-sminy-primary/30 text-blue-100' : 'bg-sminy-primary/30 text-blue-900'}`}>3-4</div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Média atividade</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs mr-3 ${isDark ? 'bg-sminy-primary/60 text-white' : 'bg-sminy-primary/60 text-white'}`}>5-8</div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Alta atividade</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs mr-3 bg-sminy-primary text-white">9+</div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pico de atividade</span>
                </div>
              </div>

              <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Os horários com tons mais fortes indicam maior volume de leads. Use estes dados para programar campanhas e disponibilidade da equipe.
              </p>

              <div className={`mt-5 rounded-xl border p-4 ${isDark ? 'border-gray-600 bg-gray-800/40' : 'border-gray-200 bg-white/70'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Insights reais do período</h4>

                {peakHeatmapSlot ? (
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Maior pico</p>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {weekdayMap[peakHeatmapSlot.diaSemana]}, {peakHeatmapSlot.faixa}h
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {peakHeatmapSlot.totalLeads} leads no slot de maior volume
                      </p>
                    </div>

                    {dominantPeriod && (
                      <div>
                        <p className={`text-xs uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Turno dominante</p>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {dominantPeriod.label}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {dominantPeriod.data.leads} leads, {dominantPeriod.data.agendamentos} agendamentos e {dominantPeriod.data.taxaAgendamento.toFixed(1)}% de conversão
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sem volume suficiente no período selecionado para gerar insight automático.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Análise por Período do Dia */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Análise por Período</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Performance agrupada por turno de atendimento</p>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Manhã */}
          <div className={`rounded-2xl border p-4 transition-all hover:shadow-md ${isDark ? 'bg-gray-700/30 border-blue-900/50' : 'bg-blue-50/50 border-blue-100'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>Manhã (8h-12h)</p>
                <h4 className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{periodData.morning.leads}</h4>
              </div>
              <div className={`p-2 rounded-xl ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                <SunIcon className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Agendamentos</span>
                <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{periodData.morning.agendamentos}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(periodData.morning.taxaAgendamento, 100)}%` }}></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conversão</span>
                <span className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                  {periodData.morning.taxaAgendamento.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Tarde */}
          <div className={`rounded-2xl border p-4 transition-all hover:shadow-md ${isDark ? 'bg-gray-700/30 border-emerald-900/50' : 'bg-emerald-50/50 border-emerald-100'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>Tarde (12h-18h)</p>
                <h4 className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{periodData.afternoon.leads}</h4>
              </div>
              <div className={`p-2 rounded-xl ${isDark ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-600'}`}>
                <SunsetIcon className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Agendamentos</span>
                <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{periodData.afternoon.agendamentos}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
                <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${Math.min(periodData.afternoon.taxaAgendamento, 100)}%` }}></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conversão</span>
                <span className={`font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                  {periodData.afternoon.taxaAgendamento.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Noite */}
          <div className={`rounded-2xl border p-4 transition-all hover:shadow-md ${isDark ? 'bg-gray-700/30 border-amber-900/50' : 'bg-amber-50/50 border-amber-100'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>Noite (18h-0h)</p>
                <h4 className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{periodData.night.leads}</h4>
              </div>
              <div className={`p-2 rounded-xl ${isDark ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-600'}`}>
                <MoonIcon className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Agendamentos</span>
                <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{periodData.night.agendamentos}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
                <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${Math.min(periodData.night.taxaAgendamento, 100)}%` }}></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conversão</span>
                <span className={`font-medium ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                  {periodData.night.taxaAgendamento.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Madrugada */}
          <div className={`rounded-2xl border p-4 transition-all hover:shadow-md ${isDark ? 'bg-gray-700/30 border-purple-900/50' : 'bg-purple-50/50 border-purple-100'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>Madrugada (0h-8h)</p>
                <h4 className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{periodData.dawn.leads}</h4>
              </div>
              <div className={`p-2 rounded-xl ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                <StarIcon className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Agendamentos</span>
                <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{periodData.dawn.agendamentos}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
                <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(periodData.dawn.taxaAgendamento, 100)}%` }}></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conversão</span>
                <span className={`font-medium ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                  {periodData.dawn.taxaAgendamento.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorariosTab;
